/* RESPONSIBLE TEAM: team-billing */

import nonEuTaxEligibleCountries from '../lib/non-eu-tax-eligible-countries';

/**
 * Validates VAT number based on country-specific rules derived from placeholder patterns
 * @param {string} vatNumber - The VAT number to validate
 * @param {string} countryCode - The country code (e.g., 'AU', 'CA', 'CH')
 * @returns {boolean} - True if VAT number is valid for the country
 */
export function validateVatNumber(vatNumber, countryCode) {
  if (!vatNumber || !countryCode) {
    return true; // VAT number is optional, so empty is valid
  }

  // If not a non-EU tax eligible country, skip validation (always valid)
  if (!nonEuTaxEligibleCountries.includes(countryCode)) {
    return true;
  }

  // Remove spaces and normalize
  let normalizedVat = vatNumber.replace(/\s/g, '');

  // Country-specific validation rules based on placeholder patterns
  switch (countryCode) {
    case 'AU': // Australia: 11 digits
      return /^\d{11}$/.test(normalizedVat);

    case 'CA': // Canada: 9 digits + RT + 4 digits (123456789RT0001)
      return /^\d{9}RT\d{4}$/.test(normalizedVat);

    case 'CH': // Switzerland: CHE + 9 digits + MWST, with dots (CHE123.456.789MWST)
      return /^CHE\d{3}\.\d{3}\.\d{3}MWST$/.test(normalizedVat);

    case 'LI': // Liechtenstein: 5 digits
      return /^\d{5}$/.test(normalizedVat);

    case 'MY': // Malaysia: Letter + 14 digits with hyphens (A12-3456-78912345)
      return /^[A-Z]\d{2}-\d{4}-\d{8}$/.test(normalizedVat);

    case 'NO': // Norway: 9 digits + MVA (123456789MVA)
      return /^\d{9}MVA$/.test(normalizedVat);

    case 'NZ': // New Zealand: 9 digits
      return /^\d{9}$/.test(normalizedVat);

    default:
      // For any other countries, skip validation
      return true;
  }
}

/**
 * Gets the translation key for validation error message for invalid VAT numbers
 * @param {string} countryCode - The country code
 * @returns {string} - Translation key for the error message
 */
export function getVatValidationErrorKey(countryCode) {
  let baseKey = 'signup.teams.pricing5.annual-plans.credit-card-details';

  let errorCountry = 'default';
  if (countryCode && nonEuTaxEligibleCountries.includes(countryCode)) {
    errorCountry = countryCode.toLowerCase();
  }

  return `${baseKey}.${errorCountry}-tax-number-error`;
}
